import { useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCorners,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { GripVertical, Calendar, MoreHorizontal } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  STATUS_LABELS,
  STATUS_COLORS,
  STATUS_ORDER,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  formatDueDate,
  isOverdue,
  initials,
  statusLabel,
  statusOrder,
} from "@/lib/taskHelpers";

export default function TaskBoard({ tasks, statuses, onUpdate, onOpen }) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Use the workspace's status list when loaded, otherwise the static
  // built-in order. Either way, columns appear for every status the
  // workspace defines so a custom "In Review" column shows up next to
  // the built-ins.
  const order = useMemo(() => statusOrder(statuses), [statuses]);

  const grouped = useMemo(() => {
    const out = Object.fromEntries(order.map((s) => [s, []]));
    tasks.forEach((t) => {
      const s = order.includes(t.status) ? t.status : (order[0] || "todo");
      if (!out[s]) out[s] = [];
      out[s].push(t);
    });
    return out;
  }, [tasks, order]);

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const taskId = active.id;
    const targetStatus = over.id;
    const task = tasks.find((t) => t.task_id === taskId);
    if (!task || task.status === targetStatus) return;
    onUpdate(taskId, { status: targetStatus });
  };

  const activeTask = activeId ? tasks.find((t) => t.task_id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="task-board">
        {order.map((s) => (
          <Column key={s} status={s} statuses={statuses} tasks={grouped[s]} onUpdate={onUpdate} onOpen={onOpen} />
        ))}
      </div>
      <DragOverlay>
        {activeTask ? <Card task={activeTask} dragging /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function Column({ status, statuses, tasks, onUpdate, onOpen }) {
  const { isOver, setNodeRef } = useDroppable({ id: status });
  const stat = STATUS_COLORS[status] || STATUS_COLORS.todo;
  // Workspace-aware label so custom statuses render with their
  // user-defined name (and chip color via the table-level call site).
  const label = statusLabel(status, statuses);
  return (
    <div
      ref={setNodeRef}
      className="bloq-card-soft p-3 flex flex-col gap-2 transition-colors"
      style={isOver ? { background: "#e5e8eb" } : {}}
      data-testid={`board-column-${status}`}
    >
      <div className="flex items-center justify-between px-1 py-1">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: stat.c }} />
          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: stat.c }}>
            {label}
          </p>
        </div>
        <span className="text-xs text-[#82828c]">{tasks.length}</span>
      </div>
      {tasks.map((t) => (
        <DraggableCard key={t.task_id} task={t} statuses={statuses} onUpdate={onUpdate} onOpen={onOpen} />
      ))}
      {tasks.length === 0 && (
        <p className="text-xs text-[#82828c] text-center py-6">Drop tasks here</p>
      )}
    </div>
  );
}

function DraggableCard({ task, statuses, onUpdate, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.task_id });
  return (
    <div
      ref={setNodeRef}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      data-testid={`board-card-${task.task_id}`}
    >
      <Card task={task} statuses={statuses} onUpdate={onUpdate} onOpen={onOpen} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function Card({ task, statuses, onUpdate, onOpen, dragHandleProps, dragging }) {
  const prio = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
  const overdue = task.status !== "done" && isOverdue(task.due_date);
  // Mobile select pulls from workspace + built-in statuses combined,
  // so custom statuses appear here too.
  const selectableStatuses = Array.isArray(statuses) && statuses.length
    ? statuses
    : STATUS_ORDER.map((k) => ({ key: k, name: STATUS_LABELS[k] }));
  return (
    <div
      className={`bloq-card p-3 cursor-pointer group ${dragging ? "shadow-2xl" : ""}`}
      onClick={(e) => { if (e.target.closest("[data-no-open]")) return; onOpen && onOpen(task.task_id); }}
    >
      <div className="flex items-start gap-2">
        <button
          {...(dragHandleProps || {})}
          data-no-open
          className="text-[#82828c] hover:text-[#1a1a1f] opacity-0 group-hover:opacity-100 mt-0.5 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-snug">{task.title}</p>
          <div className="flex items-center flex-wrap gap-1.5 mt-2">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-medium uppercase tracking-widest"
              style={{ background: prio.bg, color: prio.c }}
            >
              {PRIORITY_LABELS[task.priority] || task.priority}
            </span>
            {task.due_date && (
              <span className={`text-[10px] inline-flex items-center gap-1 ${overdue ? "text-[#dc2626] font-semibold" : "text-[#82828c]"}`}>
                <Calendar className="w-3 h-3" /> {formatDueDate(task.due_date)}
              </span>
            )}
            {task.labels?.slice(0, 2).map((lbl) => (
              <span key={lbl.label_id} className="text-[9px] font-medium rounded-full px-1.5 py-0.5" style={{ background: `${lbl.color}1a`, color: lbl.color }}>
                {lbl.name}
              </span>
            ))}
          </div>
        </div>
        {task.assignee_id && (
          <Avatar className="w-6 h-6 rounded-md flex-shrink-0" title={task.assignee_name || ""}>
            <AvatarImage src={task.assignee_avatar || ""} className="rounded-md" />
            <AvatarFallback className="bg-[#005e7a] text-white text-[9px] rounded-md">{initials(task.assignee_name)}</AvatarFallback>
          </Avatar>
        )}
      </div>
      {onUpdate && (
        <div className="sm:hidden mt-2" data-no-open>
          <Select value={task.status} onValueChange={(v) => onUpdate(task.task_id, { status: v })}>
            <SelectTrigger className="h-7 text-[11px]" data-testid={`board-status-select-${task.task_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {selectableStatuses.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
